import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import BottomNav from "@/components/BottomNav";
import Header from "@/components/Header";
import { 
  Castle, 
  PartyPopper, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar as CalendarIcon,
  Clock,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Zap,
  Check,
  AlertCircle,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Default time slots (fallback if no config)
const DEFAULT_TIME_SLOTS = [
  { id: "matin", name: "Matin", hours: "8h - 13h" },
  { id: "apres-midi", name: "Après-midi", hours: "14h - 19h" },
  { id: "journee", name: "Journée complète", hours: "8h - 19h" },
];

const SOURCES = [
  { id: "messenger", name: "Messenger" },
  { id: "telephone", name: "Téléphone" },
  { id: "manuel", name: "Manuel" },
];

export default function CreateReservationPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Catalogue data
  const [structures, setStructures] = useState([]);
  const [offers, setOffers] = useState([]);
  const [options, setOptions] = useState([]);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);
  
  // Availability data per structure
  const [structuresAvailability, setStructuresAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  
  const [formData, setFormData] = useState({
    date: null,
    time_slot: "",
    structure_id: "",
    offer_id: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    options: [],
    needs_generator: false,
    source: "manuel",
    notes: "",
  });

  useEffect(() => {
    fetchCatalogue();
    fetchTimeSlots();
  }, []);

  // Fetch availability when date or time_slot changes
  useEffect(() => {
    if (formData.date && formData.time_slot) {
      fetchAllStructuresAvailability();
    }
  }, [formData.date, formData.time_slot]);

  const fetchCatalogue = async () => {
    try {
      const response = await fetch(`${API}/catalogue`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStructures(data.structures);
        setOffers(data.offers);
        setOptions(data.options);
      }
    } catch (error) {
    }
  };

  const fetchTimeSlots = async () => {
    try {
      const response = await fetch(`${API}/settings/time-slots`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        // Convert object to array and filter enabled slots
        const slots = Object.values(data.time_slots || {})
          .filter(slot => slot.enabled)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map(slot => ({ id: slot.id, name: slot.name, hours: slot.hours }));
        if (slots.length > 0) {
          setTimeSlots(slots);
        }
      }
    } catch (error) {
    }
  };

  const fetchAllStructuresAvailability = async () => {
    if (!formData.date) return;
    
    setLoadingAvailability(true);
    const dateStr = format(formData.date, "yyyy-MM-dd");
    const availability = {};
    
    try {
      // Fetch availability for all structures in parallel
      const promises = structures.map(async (structure) => {
        const response = await fetch(
          `${API}/availability/${structure.id}?date=${dateStr}`,
          { credentials: "include" }
        );
        if (response.ok) {
          const data = await response.json();
          return { id: structure.id, data };
        }
        return { id: structure.id, data: { booked_slots: [] } };
      });
      
      const results = await Promise.all(promises);
      results.forEach(({ id, data }) => {
        availability[id] = data;
      });
      
      setStructuresAvailability(availability);
    } catch (error) {
    } finally {
      setLoadingAvailability(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    
    // Reset structure selection when date/time changes
    if (field === "date" || field === "time_slot") {
      setFormData((prev) => ({ ...prev, [field]: value, structure_id: "" }));
    }
  };

  const toggleOption = (optionId) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.includes(optionId)
        ? prev.options.filter((id) => id !== optionId)
        : [...prev.options, optionId],
    }));
  };

  const isStructureAvailable = (structureId) => {
    const availability = structuresAvailability[structureId];
    if (!availability) return true; // Assume available if not loaded
    
    const bookedSlots = availability.booked_slots || [];
    const selectedSlot = formData.time_slot;
    
    if (selectedSlot === "journee") {
      // Full day requires both slots to be free
      return !bookedSlots.includes("matin") && 
             !bookedSlots.includes("apres-midi") && 
             !bookedSlots.includes("journee");
    } else {
      // Half day is available if not booked and no full day booking
      return !bookedSlots.includes(selectedSlot) && !bookedSlots.includes("journee");
    }
  };

  // Get structure price based on time slot
  const getStructurePrice = (structure) => {
    if (!structure) return 0;
    if (formData.time_slot === "journee") {
      return structure.price_full_day || 0;
    }
    return structure.price_half_day || 0;
  };

  // Get offers compatible with selected structure
  const getCompatibleOffers = () => {
    if (!formData.structure_id) return [];
    return offers.filter(offer => {
      if (!offer.included_structures || offer.included_structures.length === 0) return false;
      return offer.included_structures.some(inc => inc.structure_id === formData.structure_id);
    });
  };

  // Get supplement for structure in selected offer
  const getStructureSupplement = () => {
    if (!formData.offer_id || !formData.structure_id) return 0;
    const offer = offers.find(o => o.id === formData.offer_id);
    if (!offer || !offer.included_structures) return 0;
    const included = offer.included_structures.find(inc => inc.structure_id === formData.structure_id);
    return included?.supplement || 0;
  };

  const calculateTotal = () => {
    let total = 0;
    const structure = structures.find(s => s.id === formData.structure_id);
    const offer = offers.find(o => o.id === formData.offer_id);
    
    if (offer) {
      // With offer: offer price + structure supplement (if any)
      total += offer.price;
      total += getStructureSupplement();
    } else if (structure) {
      // No offer: structure price based on time slot
      total += getStructurePrice(structure);
    }
    
    formData.options.forEach((optId) => {
      const opt = options.find((o) => o.id === optId);
      if (opt) total += opt.price;
    });
    
    if (formData.needs_generator && !formData.options.includes("groupe-electrogene")) {
      total += 50;
    }
    
    return total;
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...formData,
          date: format(formData.date, "yyyy-MM-dd"),
          children_count: 0, // Default value since field is removed
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de la création");
      }

      const reservation = await response.json();
      toast.success("Réservation créée en OPTION");
      navigate(`/reservations/${reservation.id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.date && formData.time_slot;
      case 2:
        return formData.structure_id;
      case 3:
        // Offer is optional - always can proceed
        return true;
      case 4:
        // Options step - always can proceed
        return true;
      case 5:
        return (
          formData.client_phone &&
          formData.client_address
        );
      default:
        return true;
    }
  };

  const steps = [
    { num: 1, label: "Date" },
    { num: 2, label: "Structure" },
    { num: 3, label: "Offre" },
    { num: 4, label: "Options" },
    { num: 5, label: "Client" },
  ];

  const availableStructuresCount = structures.filter(s => isStructureAvailable(s.id)).length;

  return (
    <div className="min-h-screen festive-gradient confetti-pattern page-container">
      <Header title="Nouvelle réservation" showBack />
      
      <main className="p-4 max-w-2xl mx-auto">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div
                className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
                  step >= s.num
                    ? "bg-gradient-to-br from-festive-pink to-festive-purple text-white"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`w-4 md:w-10 h-1 mx-1 rounded ${
                    step > s.num ? "bg-festive-pink" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Date & Time */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in">
            <Card className="card-festive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <CalendarIcon className="w-5 h-5 text-festive-pink" />
                  Date de l'événement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={(date) => updateField("date", date)}
                  locale={fr}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  className="rounded-xl border-0"
                />
              </CardContent>
            </Card>

            {formData.date && (
              <Card className="card-festive">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading">
                    <Clock className="w-5 h-5 text-festive-purple" />
                    Créneau horaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={formData.time_slot}
                    onValueChange={(v) => updateField("time_slot", v)}
                    className="grid gap-3"
                  >
                    {timeSlots.map((slot) => (
                      <Label
                        key={slot.id}
                        htmlFor={`slot-${slot.id}`}
                        className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.time_slot === slot.id
                            ? "border-festive-purple bg-purple-50"
                            : "border-gray-100 hover:border-purple-200"
                        }`}
                      >
                        <RadioGroupItem value={slot.id} id={`slot-${slot.id}`} />
                        <div className="flex-1">
                          <p className="font-semibold">{slot.name}</p>
                          <p className="text-sm text-gray-500">{slot.hours}</p>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Structure Selection (with availability) */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in">
            {/* Selected date/time reminder */}
            <Card className="card-festive bg-gradient-to-r from-festive-pink/10 to-festive-purple/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-festive-pink" />
                    <span className="font-medium">
                      {format(formData.date, "EEEE d MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <Badge variant="outline" className="bg-white">
                    {timeSlots.find(t => t.id === formData.time_slot)?.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="card-festive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Castle className="w-5 h-5 text-festive-pink" />
                  Choisir la structure
                  {loadingAvailability && (
                    <div className="w-4 h-4 border-2 border-festive-pink border-t-transparent rounded-full animate-spin ml-2"></div>
                  )}
                </CardTitle>
                <p className="text-sm text-gray-500">
                  {availableStructuresCount} structure{availableStructuresCount > 1 ? 's' : ''} disponible{availableStructuresCount > 1 ? 's' : ''} pour ce créneau
                </p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.structure_id}
                  onValueChange={(v) => updateField("structure_id", v)}
                  className="grid gap-3"
                >
                  {structures.map((structure) => {
                    const available = isStructureAvailable(structure.id);
                    
                    return (
                      <Label
                        key={structure.id}
                        htmlFor={structure.id}
                        className={`flex items-center gap-4 p-3 rounded-2xl border-2 transition-colors ${
                          !available
                            ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                            : formData.structure_id === structure.id
                            ? "border-festive-pink bg-pink-50 cursor-pointer"
                            : "border-gray-100 hover:border-pink-200 cursor-pointer"
                        }`}
                      >
                        <RadioGroupItem 
                          value={structure.id} 
                          id={structure.id} 
                          disabled={!available}
                        />
                        {/* Structure Image */}
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                          {structure.image ? (
                            <img 
                              src={getImageUrl(structure.image)} 
                              alt={structure.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Castle className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold truncate">{structure.name}</p>
                            {available ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 flex-shrink-0">
                                Indisponible
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{structure.dimensions}</p>
                          {/* Display price based on time slot */}
                          {(structure.price_half_day > 0 || structure.price_full_day > 0) && (
                            <p className="text-sm font-medium text-festive-pink mt-1">
                              {formData.time_slot === "journee" 
                                ? `${structure.price_full_day}€ / journée`
                                : `${structure.price_half_day}€ / demi-journée`
                              }
                              <span className="text-gray-400 font-normal ml-1">(location seule)</span>
                            </p>
                          )}
                        </div>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Offer Selection */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in">
            {/* Selection reminder */}
            <Card className="card-festive bg-gradient-to-r from-festive-pink/10 to-festive-purple/10">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-festive-pink" />
                    <span className="text-sm">
                      {format(formData.date, "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Castle className="w-4 h-4 text-festive-purple" />
                    <span className="text-sm font-medium">
                      {structures.find(s => s.id === formData.structure_id)?.name}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-festive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <PartyPopper className="w-5 h-5 text-festive-purple" />
                  Choisir l'offre
                </CardTitle>
                <p className="text-sm text-gray-500">Optionnel - Vous pouvez aussi louer la structure seule</p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.offer_id}
                  onValueChange={(v) => updateField("offer_id", v)}
                  className="grid gap-3"
                >
                  {/* Option: No offer - structure only */}
                  <Label
                    htmlFor="offer-none"
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                      formData.offer_id === ""
                        ? "border-green-500 bg-green-50"
                        : "border-gray-100 hover:border-green-200"
                    }`}
                  >
                    <RadioGroupItem value="" id="offer-none" />
                    <div className="flex-1">
                      <p className="font-semibold">Location seule (sans formule)</p>
                      <p className="text-sm text-gray-500">Louer uniquement la structure sélectionnée</p>
                    </div>
                    <span className="font-bold text-green-600">
                      {getStructurePrice(structures.find(s => s.id === formData.structure_id))}€
                    </span>
                  </Label>

                  {/* Compatible offers */}
                  {getCompatibleOffers().map((offer) => {
                    const supplement = offer.included_structures?.find(inc => inc.structure_id === formData.structure_id)?.supplement || 0;
                    return (
                      <Label
                        key={offer.id}
                        htmlFor={`offer-${offer.id}`}
                        className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                          formData.offer_id === offer.id
                            ? "border-festive-purple bg-purple-50"
                            : "border-gray-100 hover:border-purple-200"
                        }`}
                      >
                        <RadioGroupItem value={offer.id} id={`offer-${offer.id}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{offer.name}</p>
                            {offer.requires_validation && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                                Validation requise
                              </span>
                            )}
                            {supplement > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                +{supplement}€ supplément
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{offer.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-festive-pink">{offer.price + supplement}€</span>
                          {supplement > 0 && (
                            <p className="text-xs text-gray-400">({offer.price}€ + {supplement}€)</p>
                          )}
                        </div>
                      </Label>
                    );
                  })}

                  {getCompatibleOffers().length === 0 && (
                    <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-xl">
                      Aucune formule disponible pour cette structure
                    </p>
                  )}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Client Info */}
        {step === 5 && (
          <Card className="card-festive animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <User className="w-5 h-5 text-festive-pink" />
                Informations client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="client_name">Nom complet</Label>
                <Input
                  id="client_name"
                  data-testid="input-client-name"
                  value={formData.client_name}
                  onChange={(e) => updateField("client_name", e.target.value)}
                  className="input-festive mt-1"
                  placeholder="Jean Dupont"
                />
              </div>

              <div>
                <Label htmlFor="client_phone">Téléphone *</Label>
                <Input
                  id="client_phone"
                  data-testid="input-client-phone"
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => updateField("client_phone", e.target.value)}
                  className="input-festive mt-1"
                  placeholder="0692 XX XX XX"
                />
              </div>

              <div>
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  data-testid="input-client-email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => updateField("client_email", e.target.value)}
                  className="input-festive mt-1"
                  placeholder="jean@example.com"
                />
              </div>

              <div>
                <Label htmlFor="client_address">Adresse complète *</Label>
                <Textarea
                  id="client_address"
                  data-testid="input-client-address"
                  value={formData.client_address}
                  onChange={(e) => updateField("client_address", e.target.value)}
                  className="input-festive mt-1 min-h-[80px]"
                  placeholder="123 Rue Example, 97400 Saint-Denis"
                />
              </div>

              <div>
                <Label htmlFor="source">Source de la demande</Label>
                <Select
                  value={formData.source}
                  onValueChange={(v) => updateField("source", v)}
                >
                  <SelectTrigger data-testid="select-source" className="input-festive mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Options */}
        {step === 4 && (
          <div className="space-y-4 animate-fade-in">
            <Card className="card-festive">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <Sparkles className="w-5 h-5 text-festive-yellow" />
                  Options complémentaires
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {options.map((option) => (
                  <Label
                    key={option.id}
                    htmlFor={`option-${option.id}`}
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                      formData.options.includes(option.id)
                        ? "border-festive-yellow bg-yellow-50"
                        : "border-gray-100 hover:border-yellow-200"
                    }`}
                  >
                    <Checkbox
                      id={`option-${option.id}`}
                      checked={formData.options.includes(option.id)}
                      onCheckedChange={() => toggleOption(option.id)}
                    />
                    <div className="flex-1">
                      <p className="font-semibold">{option.name}</p>
                      {option.description && (
                        <p className="text-sm text-gray-500">{option.description}</p>
                      )}
                    </div>
                    <span className="font-bold text-festive-pink">+{option.price}€</span>
                  </Label>
                ))}
              </CardContent>
            </Card>

            <Card className="card-festive">
              <CardContent className="p-4">
                <Label
                  htmlFor="needs_generator"
                  className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
                    formData.needs_generator
                      ? "border-festive-cyan bg-cyan-50"
                      : "border-gray-100 hover:border-cyan-200"
                  }`}
                >
                  <Checkbox
                    id="needs_generator"
                    checked={formData.needs_generator}
                    onCheckedChange={(checked) => updateField("needs_generator", checked)}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Zap className="w-5 h-5 text-festive-cyan" />
                    <div>
                      <p className="font-semibold">Groupe électrogène</p>
                      <p className="text-sm text-gray-500">Si pas d'accès électrique</p>
                    </div>
                  </div>
                  <span className="font-bold text-festive-pink">+50€</span>
                </Label>
              </CardContent>
            </Card>

            <Card className="card-festive">
              <CardHeader>
                <CardTitle className="font-heading">Notes additionnelles</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  data-testid="input-notes"
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className="input-festive min-h-[100px]"
                  placeholder="Informations supplémentaires..."
                />
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="card-festive bg-gradient-to-br from-festive-pink/10 to-festive-purple/10">
              <CardContent className="p-4">
                <h3 className="font-heading font-bold text-lg mb-3">Récapitulatif</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Date</span>
                    <span className="font-medium">
                      {formData.date && format(formData.date, "d MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Créneau</span>
                    <span className="font-medium">
                      {timeSlots.find((t) => t.id === formData.time_slot)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Structure</span>
                    <span className="font-medium">
                      {structures.find((s) => s.id === formData.structure_id)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Offre</span>
                    <span className="font-medium">
                      {offers.find((o) => o.id === formData.offer_id)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Client</span>
                    <span className="font-medium">{formData.client_name}</span>
                  </div>
                  {formData.options.length > 0 && (
                    <div className="flex justify-between">
                      <span>Options</span>
                      <span className="font-medium text-right">
                        {formData.options
                          .map((id) => options.find((o) => o.id === id)?.name)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  <div className="border-t border-pink-200 pt-2 mt-2 flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="text-festive-pink">{calculateTotal()}€</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12 rounded-xl border-2 border-pink-200"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
          )}
          
          {step < 5 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-xl"
              data-testid="next-step-btn"
            >
              Suivant
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !formData.client_phone || !formData.client_address}
              className="flex-1 h-12 bg-gradient-to-r from-festive-pink to-festive-purple hover:opacity-90 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-reservation-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5 mr-1" />
                  Créer en OPTION
                </>
              )}
            </Button>
          )}
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
